import { type TargetedEvent } from 'preact/compat'
import { navigate } from 'wouter-preact/use-browser-location'
import { createState } from 'shared'
import { DAppService, ForManagingDApps, GuardInput } from 'core'

import {
	addDAppToProjectDetail,
	application,
	emitSystemNotification,
	projectDetailsState,
	resetSystemNotification
} from 'state'

const dAppNameStateContainer = createState('')
const dAppDescriptionStateContainer = createState('')
const githubUrlStateContainer = createState('')
const dAppFormErrorsState = createState<Record<string, string | undefined>>({})
const isDAppLoadingState = createState(false)

export const dAppNameState = dAppNameStateContainer.state
export const dAppDescriptionState = dAppDescriptionStateContainer.state
export const githubUrlState = githubUrlStateContainer.state
export const formErrors = dAppFormErrorsState.state
export const isDAppLoading = isDAppLoadingState.state

const onUpdateInputErrors = (field: string, error?: string) => {
	dAppFormErrorsState.updateState(prevErrorState => {
		if (prevErrorState[field] === error) return prevErrorState
		return {
			...prevErrorState,
			[field]: error
		}
	})
}

dAppNameStateContainer.onUpdateState(value => {
	const dapps = projectDetailsState.peek()?.dapps || []
	const error = DAppService.ValidateDAppName(value, dapps)

	if (error) {
		onUpdateInputErrors('name', error.inputErrors?.name)
	} else if (formErrors.peek().name !== '') {
		onUpdateInputErrors('name', '')
	}
})

dAppDescriptionStateContainer.onUpdateState(value => {
	const error = GuardInput.check(
		ForManagingDApps.CreateDAppBasePayload.partial(),
		{
			description: value
		}
	)

	if (error) {
		onUpdateInputErrors('description', error.inputErrors?.description)
	} else if (formErrors.peek().description !== '') {
		onUpdateInputErrors('description', '')
	}
})

githubUrlStateContainer.onUpdateState(value => {
	if (!value) {
		onUpdateInputErrors('githubUrl', '')
		return
	}

	const error = GuardInput.check(
		ForManagingDApps.CreateDAppFromGithubPayload.partial(),
		{
			githubUrl: value
		}
	)

	if (error) {
		onUpdateInputErrors('githubUrl', error.inputErrors?.githubUrl)
	} else if (formErrors.peek().githubUrl !== '') {
		onUpdateInputErrors('githubUrl', '')
	}
})

export const setDAppName = (value: string) => {
	dAppNameStateContainer.updateState(() => value)
}

export const setDAppDescription = (value: string) => {
	dAppDescriptionStateContainer.updateState(() => value)
}

export const setGithubUrl = (value: string) => {
	githubUrlStateContainer.updateState(() => value)
}

export const resetDAppForm = () => {
	dAppNameStateContainer.updateState(() => '')
	dAppDescriptionStateContainer.updateState(() => '')
	dAppFormErrorsState.updateState(() => ({}))
}

export const createDAppFromGithub = async (
	event: TargetedEvent<HTMLFormElement, Event>
): Promise<void> => {
	event.preventDefault()
	const projectId = projectDetailsState.peek()!.publicId!
	isDAppLoadingState.updateState(() => true)
	resetSystemNotification()

	const { data, error } = await application.value
		.forManagingDApps()
		.createDAppFromGithub({
			name: dAppNameState.peek()!,
			description: dAppDescriptionState.peek()!,
			githubUrl: githubUrlState.peek()!,
			publicProjectId: projectId
		})

	isDAppLoadingState.updateState(() => false)

	if (error) {
		emitSystemNotification({ error, display: 'dialog' })
		return
	}

	addDAppToProjectDetail(projectId, data!)

	navigate(`/dapp/${projectId}/smart-contract/${data!.name}/workspace`)

	resetDAppForm()
}
